import RestClientService from "@/core/services/restClient.service";
import { IFDG } from "@/core/interfaces/state/fdg.interface";
import { GroupFlightsResponseModel } from "@/modules/FDG/models/fdgList.response";
import { ICoreResponse } from "@/core/interfaces/coreResponse.interface";
import { FDG_FLIGHTS_API_PATH } from "@/modules/FDG/constants";
import { FdgMapper } from "@/modules/FDG/responseMappers/fdg.mapper";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_STYLES } from "@/core/enums/NOTIFICATION_STYLES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { formatString } from "@/core/utils/helpers";
import { GroupFlightDetailResponseModel } from "@/modules/FDG/models/fdgDetail.response";
import { IFDGDetail } from "@/core/interfaces/state/fdgDetail.interface";
import {
  IFDGBookingDetail,
  IFDGBookingPassenger,
} from "@/core/interfaces/state/fdgBookingDetail.interface";
import { FDGBookingDetailResponseModel } from "@/modules/FDG/models/fdgBookingDetail.response";
import { FDGBookingDetailRequestModel } from "@/modules/FDG/models/fdgBookingDetail.request";
import { FDGBookingConfirmRequestModel } from "@/modules/FDG/models/fdgBookingConfirm.request";
import { FDGBookingConfirmWithoutTravelerDetailsRequestModel } from "@/modules/FDG/models/fdgBookingConfirmWithoutTravelerDetails.request";
import { TOUR_TYPES } from "@/core/enums/TOUR_TYPE";
import { ActionContext } from "vuex";
import { IFDGState } from "@/core/interfaces/state/fdgState.interface";
import { StoreState } from "@/store/type";
import { FDGUpdatePassengerRequest } from "../models/fdgUpdatePassenger.request";
import {
  FDGPayNowRequest,
  FDGCancelRequest,
} from "../models/fdgPaynow.request";

class FDGService {
  private restClient: RestClientService;

  constructor() {
    this.restClient = new RestClientService();
  }

  public async fetchGroupFlights(tourType: TOUR_TYPES): Promise<IFDG[]> {
    let mappedGroupFlights: IFDG[] = [];
    try {
      const response: ICoreResponse<GroupFlightsResponseModel> =
        await this.restClient.get(
          `${FDG_FLIGHTS_API_PATH.GET}/?tour_type=${tourType}`,
        );

      if (response.success && response.data) {
        const fdgListMapper = new FdgMapper();
        mappedGroupFlights = fdgListMapper.mapFDGListResponse(response.data);
      } else {
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
      notificationService.description = NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
    return mappedGroupFlights;
  }

  public async fetchGroupFlightDetail(id: string): Promise<IFDGDetail | null> {
    let mappedFDGDetail: IFDGDetail | null = null;
    try {
      const response: ICoreResponse<GroupFlightDetailResponseModel> =
        await this.restClient.get(
          formatString(FDG_FLIGHTS_API_PATH.GET_BY_ID, id),
        );

      if (response.success && response.data) {
        const fdgListMapper = new FdgMapper();
        mappedFDGDetail = fdgListMapper.mapFDGDetailResponse(response.data);
      } else {
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
      notificationService.description = NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
    return mappedFDGDetail;
  }

  public async fetchGroupFlightBookingDetail(
    id: string,
  ): Promise<IFDGBookingDetail | null> {
    let mappedGroupFlightBookingDetail: IFDGBookingDetail | null = null;
    try {
      const response: ICoreResponse<FDGBookingDetailResponseModel> =
        await this.restClient.get(
          formatString(FDG_FLIGHTS_API_PATH.GET_BOOKING_BY_ID, id),
        );

      if (response.success && response.data) {
        const fdgListMapper = new FdgMapper();
        mappedGroupFlightBookingDetail =
          fdgListMapper.mapFDGBookingDetailResponse(response.data);
      } else {
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
      notificationService.description = NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
    return mappedGroupFlightBookingDetail;
  }

  public async initiateBooking(
    payload: FDGBookingDetailRequestModel,
    callback: (bookingId: string) => void,
  ): Promise<IFDGBookingDetail | null> {
    let mappedGroupFlightBookingDetail: IFDGBookingDetail | null = null;
    try {
      const response: ICoreResponse<FDGBookingDetailResponseModel> =
        await this.restClient.post(
          FDG_FLIGHTS_API_PATH.POST_INITIATE_BOOKING,
          payload,
        );

      if (response.success && response.data) {
        const fdgListMapper = new FdgMapper();
        mappedGroupFlightBookingDetail =
          fdgListMapper.mapFDGBookingDetailResponse(response.data);
        callback(mappedGroupFlightBookingDetail?.bookingId || "");
      } else {
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
      notificationService.description = NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
    return mappedGroupFlightBookingDetail;
  }

  public async confirmBooking(
    payload: FDGBookingConfirmRequestModel,
    callback: () => void,
  ) {
    try {
      const response: ICoreResponse = await this.restClient.post(
        FDG_FLIGHTS_API_PATH.POST_CONFIRM_BOOKING,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (response.success) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.description =
          NOTIFICATION_MESSAGES.FDG_FLIGHTS_CONFIRM_BOOKING_SUCCESS;
        callback();
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
      }
    } catch {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
      notificationService.description = NOTIFICATION_MESSAGES.DEFAULT;
    }
    notificationService.triggerNotification();
  }

  public async confirmGroupFlightBookingWithoutTravelerDetails(
    payload: FDGBookingConfirmWithoutTravelerDetailsRequestModel,
    callback: () => void,
  ) {
    try {
      const response: ICoreResponse = await this.restClient.post(
        FDG_FLIGHTS_API_PATH.POST_CONFIRM_BOOKING_WITHOUT_TRAVELER_DETAILS,
        payload,
      );
      if (response.success) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.description =
          NOTIFICATION_MESSAGES.FDG_FLIGHTS_CONFIRM_BOOKING_SUCCESS;
        callback();
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
      }
    } catch {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
      notificationService.description = NOTIFICATION_MESSAGES.DEFAULT;
    }
    notificationService.triggerNotification();
  }

  public async generateGroupFlightBookingOTP(
    booking_id: string,
    callback: () => void,
    context: ActionContext<IFDGState, StoreState>,
  ) {
    let otpReferenceId = "";
    try {
      const response: ICoreResponse = await this.restClient.post(
        FDG_FLIGHTS_API_PATH.GENERATE_OTP,
        { booking_id },
      );
      if (response.success) {
        otpReferenceId = response.data?.data?.otp_reference_id;
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.description =
          NOTIFICATION_MESSAGES.FDG_FLIGHTS_OTP_SENT_TO_EMAIL_SUCCESS;
        callback();
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
      }
    } catch {
      context.commit("setOTPVerificationInProgress", false);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
      notificationService.description = NOTIFICATION_MESSAGES.DEFAULT;
    }
    notificationService.triggerNotification();
    return otpReferenceId;
  }

  public async getGroupBookingPassengers(booking_id: string) {
    let passengers: IFDGBookingPassenger[] = [];

    try {
      const response: ICoreResponse = await this.restClient.get(
        formatString(
          FDG_FLIGHTS_API_PATH.GROUP_FLIGHTS_BOOKING_PASSENGERS,
          booking_id,
        ),
      );
      if (response.success) {
        passengers = response.data?.data;
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
      }
    } catch {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
      notificationService.description = NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
    return passengers;
  }
  public async generateGroupFlightCancelOTP(
    booking_id: string,
    callback: () => void,
    context: ActionContext<IFDGState, StoreState>,
  ): Promise<string> {
    let otpReferenceId = "";
    try {
      const response: ICoreResponse = await this.restClient.post(
        FDG_FLIGHTS_API_PATH.CANCEL_OTP,
        { booking_id },
      );
      context.commit("setOTPVerificationInProgress", true);
      if (response.success) {
        otpReferenceId = response.data?.data?.otp_reference_id;
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.description =
          NOTIFICATION_MESSAGES.FDG_FLIGHTS_OTP_SENT_TO_EMAIL_SUCCESS;
        callback();
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
      }
    } catch {
      context.commit("setOTPVerificationInProgress", false);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
      notificationService.description = NOTIFICATION_MESSAGES.DEFAULT;
    }
    notificationService.triggerNotification();
    return otpReferenceId;
  }

  public async updateGroupBookingPassengers(
    booking_id: string,
    payload: FDGUpdatePassengerRequest,
  ) {
    try {
      const response: ICoreResponse = await this.restClient.patch(
        formatString(
          FDG_FLIGHTS_API_PATH.GROUP_FLIGHTS_BOOKING_PASSENGERS,
          booking_id,
        ),
        payload,
      );
      if (response.success) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.description =
          NOTIFICATION_MESSAGES.FDG_FLIGHTS_PASSENGER_UPDATE_SUCCESS;
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
      }
    } catch {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
      notificationService.description = NOTIFICATION_MESSAGES.DEFAULT;
    }
    notificationService.triggerNotification();
  }

  public async groupFlightsPayNow(
    payload: FDGPayNowRequest,
    callback: () => void,
  ) {
    try {
      const response: ICoreResponse = await this.restClient.post(
        FDG_FLIGHTS_API_PATH.GROUP_FLIGHTS_BOOKING_PAY_NOW,
        payload,
      );
      if (response.success) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.description =
          NOTIFICATION_MESSAGES.FDG_FLIGHTS_PAY_NOW_SUCCESS;
        callback();
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
      }
    } catch {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
      notificationService.description = NOTIFICATION_MESSAGES.DEFAULT;
    }
    notificationService.triggerNotification();
  }

  public async groupFlightsCancel(
    payload: FDGCancelRequest,
    callback: () => void,
  ) {
    try {
      const response: ICoreResponse = await this.restClient.post(
        FDG_FLIGHTS_API_PATH.CANCEL_TOUR,
        payload,
      );
      if (response.success) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.description =
          NOTIFICATION_MESSAGES.FDG_FLIGHTS_CANCEL_SUCCESS;
        callback();
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
      }
    } catch {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
      notificationService.description = NOTIFICATION_MESSAGES.DEFAULT;
    }
    notificationService.triggerNotification();
  }
}

export default FDGService;
